/**
 * @module Service
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Page, PageRequest } from '@app/models/page.model';

/**
 * Abstract base service for handling HTTP operations in the application.
 *
 * This class provides common functionality for making HTTP requests, including methods for
 * parameter construction and type casting of response objects. It is intended to be extended
 * by other services that require HTTP capabilities.
 *
 * @abstract
 * @class
 */
export abstract class BaseHttpService {

	/**
	 * The default page size for paginated requests.
	 * @const {number}
	 */
	public readonly PAGE_SIZE = 50;

	/**
	 * The base URL for API requests.
	 * @type {string}
	 */
	protected baseUrl: string;

	/**
	 * Configuration object for HTTP headers.
	 * @type {{ headers: HttpHeaders }}
	 */
	protected config: { headers: HttpHeaders; } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the BaseHttpService.
	 *
	 * @constructor
	 * @param {HttpClient} http - The HttpClient used for making HTTP requests.
	 */
	protected constructor(protected http: HttpClient) {}

	/**
	 * Constructs HTTP parameters for pagination and search.
	 *
	 * This method creates an instance of HttpParams based on the provided pageable object
	 * and optional search term. It appends pagination and sorting parameters to the request.
	 *
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter results.
	 * @returns {HttpParams} The constructed HTTP parameters.
	 */
	public params(pageable?: PageRequest, search?: string): HttpParams {
		if (! pageable) pageable = { page: 0, size: this.PAGE_SIZE, sortParams: [] };

		let params = new HttpParams();
		params = params.append('page', pageable.page.toString());
		params = params.append('size', pageable.size.toString());

		pageable.sortParams.forEach(sortParam => params = params.append('sort', sortParam));

		if (search) {
			params = params.append('search', search);
		}

		return params;
	}

	/**
	 * Casts an observable to a specified type.
	 *
	 * This method transforms the emitted values of the observable to the specified type,
	 * applying any additional properties if provided.
	 *
	 * @template T - The generic type parameter.
	 * @param {Observable<T>} obs - The observable to cast.
	 * @param {new()} type - The constructor function for the target type.
	 * @param {Record<string, { new(): any; }>} [properties={}] - Optional properties to apply during casting.
	 * @returns {Observable<T>} An observable of the specified type.
	 */
	protected typeCast<T>(
		obs: Observable<T>,
		type: { new(): T; },
		properties: Record<string, { new(): any; }> = {}
	): Observable<T> {
		return obs.pipe(map(obj => this.typeCastObject(obj, type, properties)));
	}

	/**
	 * Casts an observable array to a specified type.
	 *
	 * This method transforms the emitted array values of the observable to the specified type,
	 * applying any additional properties if provided.
	 *
	 * @template T - The generic type parameter.
	 * @param {Observable<Array<T>>} obs - The observable array to cast.
	 * @param {new()} type - The constructor function for the target type.
	 * @param {Record<string, { new(): any; }>} [properties={}] - Optional properties to apply during casting.
	 * @returns {Observable<Array<T>>} An observable of the specified type array.
	 */
	protected typeCastArray<T>(
		obs: Observable<Array<T>>,
		type: { new(): T; },
		properties: Record<string, { new(): any; }> = {}
	): Observable<Array<T>> {
		return obs.pipe(map(arr => arr.map(obj => this.typeCastObject(obj, type, properties))));
	}

	/**
	 * Casts a paginated observable to a specified type.
	 *
	 * This method transforms the emitted page values of the observable to the specified type,
	 * applying any additional properties if provided.
	 *
	 * @template T - The generic type parameter.
	 * @param {Observable<Page<T>>} obs - The observable page to cast.
	 * @param {new()} type - The constructor function for the target type.
	 * @param {Record<string, { new(): any; }>} [properties={}] - Optional properties to apply during casting.
	 * @returns {Observable<Page<T>>} An observable of the specified type page.
	 */
	protected typeCastPage<T>(
		obs: Observable<Page<T>>,
		type: { new(): T; },
		properties: Record<string, { new(): any; }> = {}
	): Observable<Page<T>> {
		return obs.pipe(map(page => {
			page.content = page.content.map(obj => this.typeCastObject(obj, type, properties));
			return page;
		}));
	}

	/**
	 * Casts an object to a specified type, applying additional properties if provided.
	 *
	 * @template T - The generic type parameter.
	 * @param {T} obj - The object to cast.
	 * @param {new()} type - The constructor function for the target type.
	 * @param {Record<string, { new(): any; }>} [properties={}] - Optional properties to apply during casting.
	 * @returns {T} The cast object of the specified type.
	 */
	private typeCastObject<T>(
		obj: T,
		type: { new(): T; },
		properties: Record<string, { new(): any; }> = {}
	): T {
		// eslint-disable-next-line new-cap
		const result = Object.assign(new type(), obj);
		for (const key in properties) {
			if (Object.prototype.hasOwnProperty.call(result, key)) {
				const childType = properties[key];
				if (result[key] instanceof Array) {
					// eslint-disable-next-line new-cap
					result[key] = result[key].map((child: any) => Object.assign(new childType(), child));
				} else {
					// eslint-disable-next-line new-cap
					result[key] = Object.assign(new childType(), result[key]);
				}
			}
		}
		return result;
	}
}
